<template>
  <Dialog :open="true" @update:open="handleDialogChange">
    <DialogContent class="sm:max-w-2xl">
      <DialogHeader class="relative">
        <DialogTitle>Manage Note</DialogTitle>
        <DialogClose class="absolute right-2 top-2" />
      </DialogHeader>

      <form class="space-y-6" @submit.prevent="handleSubmit">
        <!-- Recording controls -->
        <div class="flex flex-col gap-3 p-4 bg-muted/30 rounded-lg">
          <div class="flex items-center gap-4">
            <Button
              type="button"
              :variant="isRecording ? 'destructive' : 'outline'"
              size="icon"
              class="shrink-0"
              @click="toggleRecording"
            >
              <Icon v-if="!isRecording" name="heroicons:microphone" class="h-5 w-5" />
              <Icon v-else name="heroicons:stop-circle" class="h-5 w-5" />
            </Button>

            <div class="flex items-center gap-2">
              <Badge variant="secondary" class="text-xs font-medium">BETA</Badge>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button type="button" variant="ghost" size="icon" class="h-6 w-6 p-0">
                      <Icon name="heroicons:information-circle" class="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" class="max-w-[200px]">
                    <p class="text-xs">
                      Record audio notes to automatically generate intentions, important points, and
                      priority level. Maximum recording time is 1 minute.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div v-if="isRecording" class="ml-auto flex items-center gap-2">
              <span class="text-sm font-medium tabular-nums">
                {{ formatTime(recordingTime) }}/1:00
              </span>
            </div>
          </div>

          <!-- Audio visualization -->
          <div v-if="isRecording" class="space-y-2">
            <!-- Level bars -->
            <div class="grid grid-cols-32 gap-px h-8 bg-muted rounded overflow-hidden">
              <div
                v-for="n in 32"
                :key="n"
                class="bg-primary/80 transition-transform duration-75"
                :style="{
                  transform: `scaleY(${Math.max(
                    0.1,
                    Math.min(1, (audioLevel + (Math.random() * 10 - 5)) / 100)
                  )})`,
                  transformOrigin: 'bottom',
                }"
              />
            </div>

            <!-- Progress bar -->
            <div class="flex items-center gap-2">
              <div class="h-1.5 flex-1 bg-muted rounded-full overflow-hidden">
                <div
                  class="h-full bg-primary transition-all duration-75"
                  :style="{ width: `${(recordingTime / 60000) * 100}%` }"
                />
              </div>
              <span class="text-sm font-medium tabular-nums">
                {{ formatTime(recordingTime) }}/1:00
              </span>
            </div>
          </div>
        </div>

        <!-- Rest of the form -->
        <div class="space-y-4">
          <div class="space-y-2">
            <Label>Intentions</Label>
            <Textarea v-model="noteData.intentions" rows="3" />
          </div>

          <div class="space-y-2">
            <Label>Important</Label>
            <Textarea v-model="noteData.important" rows="3" />
          </div>

          <div class="space-y-2">
            <Label>Priority</Label>
            <Select v-model="priorityValue">
              <SelectTrigger class="w-full">
                <SelectValue :placeholder="`Priority ${noteData.priority}`" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="n in 10" :key="n" :value="String(n)">
                  Priority {{ n }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div class="flex items-center gap-4 pt-4">
          <Button
            v-if="noteData.id"
            type="button"
            variant="destructive"
            size="sm"
            @click="deleteNote"
          >
            Delete
          </Button>
          <Button type="submit" class="ml-auto">Save Note</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useNotesStore } from '@/stores/notes'
import { useToast } from '@/components/ui/toast/use-toast'
import { Button } from '@/components/ui/button'
import { useAudioNoteStore } from '@/stores/audioNote'
import type { Note, NoteAdd, NoteUpdate } from '@/stores/notes'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLogger } from '@/composables/useLogger'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import type { AudioNoteResponse } from '@/types/audio'

interface NoteFormData {
  id: number | null
  intentions: string
  important: string
  priority: number
}

const props = defineProps<{
  bubbleUuid: string
  note?: Note
}>()

const emit = defineEmits<{
  close: []
}>()

const notesStore = useNotesStore()
const { toast } = useToast()
const audioNoteStore = useAudioNoteStore()
const { log, logError } = useLogger()

const noteData = ref<NoteFormData>({
  id: props.note?.id || null,
  intentions: props.note?.intentions || '',
  important: props.note?.important || '',
  priority: props.note?.priority || 5,
})

const existingNote = ref<Note | null>(null)
const isRecording = computed(() => audioNoteStore.isRecording)
const recordingTime = computed(() => audioNoteStore.recordingTime)

// Get audio level from the store
const audioLevel = computed(() => audioNoteStore.audioLevel || 0)

const priorityValue = computed({
  get: () => String(noteData.value.priority),
  set: (value: string) => {
    noteData.value.priority = Number(value)
  },
})

onMounted(() => {
  log('NoteModal mounted for bubble:', props.bubbleUuid)
  // Set the current bubble UUID in the audio note store
  audioNoteStore.setBubbleUuid(props.bubbleUuid)

  // Load existing note
  loadExistingNote()
})

const loadExistingNote = async () => {
  try {
    log('Loading existing note for bubble:', props.bubbleUuid)
    const note = await notesStore.getNoteByBubbleUuid(props.bubbleUuid)
    if (note) {
      log('Found existing note:', note)
      existingNote.value = note
      noteData.value = {
        id: note.id,
        intentions: note.intentions || '',
        important: note.important || '',
        priority: note.priority || 5,
      }
    } else {
      log('No existing note found for bubble:', props.bubbleUuid)
    }
  } catch (error) {
    logError('Error loading existing note:', error)
  }
}

const saveNote = async () => {
  try {
    log('Saving note for bubble:', props.bubbleUuid)
    let result
    if (existingNote.value) {
      const updateData: NoteUpdate = {
        intentions: noteData.value.intentions,
        important: noteData.value.important,
        priority: Number(noteData.value.priority),
      }
      log('Updating existing note:', updateData)
      result = await notesStore.updateNote(existingNote.value.id, updateData)
    } else {
      const noteToAdd: NoteAdd = {
        bubbleUuid: props.bubbleUuid,
        intentions: noteData.value.intentions,
        important: noteData.value.important,
        priority: Number(noteData.value.priority),
      }
      log('Adding new note:', noteToAdd)
      result = await notesStore.addNote(noteToAdd)
    }

    if (result.success) {
      log('Note saved successfully')
      toast({
        title: 'Success',
        description: existingNote.value ? 'Note updated successfully' : 'Note added successfully',
      })
      closeModal()
    } else {
      throw new Error(result.error)
    }
  } catch (error) {
    logError('Error saving note:', error)
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to save note',
      variant: 'destructive',
    })
  }
}

const deleteNote = async () => {
  if (!existingNote.value) return

  try {
    log('Deleting note:', existingNote.value.id)
    const result = await notesStore.deleteNote(existingNote.value.id)
    if (result.success) {
      log('Note deleted successfully')
      toast({
        title: 'Success',
        description: 'Note deleted successfully',
      })
      closeModal()
    } else {
      throw new Error(result.error)
    }
  } catch (error) {
    logError('Error deleting note:', error)
    toast({
      title: 'Error',
      description: error instanceof Error ? error.message : 'Failed to delete note',
      variant: 'destructive',
    })
  }
}

const closeModal = () => {
  log('Closing note modal')
  emit('close')
}

const toggleRecording = async () => {
  try {
    if (isRecording.value) {
      log('Stopping recording')
      audioNoteStore.stopRecording()
    } else {
      log('Starting recording')
      await audioNoteStore.startRecording((response) => {
        log('Received audio analysis response:', response)
        // Update form with analyzed data
        handleAnalyzedData(response)
      })
    }
  } catch (error) {
    logError('Error in toggleRecording:', error)
    let errorMessage = 'Failed to toggle recording'

    if (error instanceof Error) {
      if (error.message.includes('Microphone access was denied')) {
        errorMessage = 'Microphone access was denied. Please enable it in your browser settings.'
      } else if (error.message.includes('No microphone found')) {
        errorMessage = 'No microphone found. Please connect a microphone and try again.'
      }
    }

    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive',
    })
  }
}

const handleAnalyzedData = (response: AudioNoteResponse) => {
  noteData.value = {
    id: null,
    intentions: response.intentions || '',
    important: response.important || '',
    priority: 5, // Default to middle priority
  }
}

const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const handleSubmit = async (event: Event) => {
  event.preventDefault()
  await saveNote()
}

const handleDialogChange = (open: boolean) => {
  if (!open) {
    closeModal()
  }
}
</script>

<style scoped>
.grid-cols-32 {
  grid-template-columns: repeat(32, minmax(0, 1fr));
}

/* Add smooth animation for bars */
@keyframes pulse {
  0%,
  100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

.bg-primary\/80 {
  animation: pulse 2s ease-in-out infinite;
  animation-delay: calc(var(--index) * 0.1s);
}
</style>
